import React, { useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { Calendar } from "primereact/calendar";
import { InputSwitch } from "primereact/inputswitch";
import { Button } from "primereact/button";
import type { ReportingOptions, WatchlistUnit } from "../../../types/stationPerformance";
import { useWatchlist } from "../hooks/useWatchlist";
import { daysInMonth } from "../utils/projection";
import MeasureValueCell from "./MeasureValueCell";
import ChangePercentChip from "./ChangePercentChip";
import ChangeBandLegend from "./ChangeBandLegend";
import DefinitionContextBanner from "./DefinitionContextBanner";
import NeverComputedNotice from "./NeverComputedNotice";
import PaceWindowSummary from "./PaceWindowSummary";

interface UnderperformanceWatchlistProps {
  options: ReportingOptions;
  /** The page's selected month, `YYYY-MM`. */
  month: string;
  /** True when `month` is the running Berlin month. */
  isCurrentMonth: boolean;
  onSelectUnit: (unitId: string) => void;
}

const MEASURE_LABELS: Record<string, string> = {
  sessions: "Sessions",
  energyKwh: "Energy",
  moneyPerPaidSessionCents: "€ / paid session",
};

const euro = (cents: number) =>
  (cents / 100).toLocaleString(undefined, { style: "currency", currency: "EUR" });

const toIsoDate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

/**
 * The pace-adjusted underperformance watchlist (US4).
 *
 * The design of this screen is mostly about what it refuses to say:
 *
 * - Money is judged per **paid** session, so a station switched to free charging reads "not applicable"
 *   rather than "collapsed". Judging total cash would park every deliberately-free station permanently at
 *   the top, which is how a watchlist becomes noise and gets ignored.
 * - A material free-share move is shown as its own signal, visually distinct from a flag. It explains a
 *   money change; it is not itself a failure.
 * - Total cash is shown as context and never drives a flag.
 * - Each measure is flagged independently. There is no single verdict, because "this station is bad" is
 *   not a thing the data supports.
 */
const UnderperformanceWatchlist: React.FC<UnderperformanceWatchlistProps> = ({
  options,
  month,
  isCurrentMonth,
  onSelectUnit,
}) => {
  // The day is remembered together with the month it was picked for. A pick from another month is simply
  // ignored (derived, not reset in an effect), so a month change never fires a request with a stale day.
  const [picked, setPicked] = useState<{ month: string; date: Date } | null>(null);
  const asOfDate = picked && picked.month === month ? picked.date : null;
  const setAsOfDate = (date: Date | null) => setPicked(date ? { month, date } : null);
  // The picker only offers days of the page month, and never a day that has not happened yet.
  const { monthStart, monthEnd } = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const end = new Date(y, m - 1, daysInMonth(month));
    const today = new Date();
    return { monthStart: new Date(y, m - 1, 1), monthEnd: end > today ? today : end };
  }, [month]);
  const [flaggedOnly, setFlaggedOnly] = useState(true);
  // A picked day always wins. Otherwise a finished month is judged as at its last day; the running month
  // sends nothing, which the server reads as today.
  const asOf = asOfDate
    ? toIsoDate(asOfDate)
    : isCurrentMonth
      ? null
      : `${month}-${String(daysInMonth(month)).padStart(2, "0")}`;

  const { report, initialLoading, refreshing, neverComputed, error, fetchWatchlist } = useWatchlist({
    asOf,
    options,
    flaggedOnly,
    threshold: null,
  });

  const verdictBody = (unit: WatchlistUnit, measure: string) => {
    const verdict = unit.measures?.[measure];
    if (!verdict) return <span className="text-color-secondary">—</span>;
    const isMoney = measure === "moneyPerPaidSessionCents";
    return (
      <div className="flex flex-column">
        <span className="flex align-items-center gap-2">
          <MeasureValueCell measure={verdict.current} format={isMoney ? "money" : "number"} decimals={1} />
          {verdict.flagged && <Tag value="down" severity="danger" />}
        </span>
        <span className="text-color-secondary text-xs flex align-items-center gap-1 mt-1">
          {/* Same bands as the station list, so the scale only has to be learned once. Compared against
              the same window last month, not against the whole month, which is what makes an early-month
              figure mean anything at all. */}
          <ChangePercentChip
            percent={verdict.changePercent}
            size="small"
            hint="Against the same stretch of days last month"
          />
          {verdict.basis === "PAID_SESSIONS_ONLY" && (
            <i
              className="pi pi-info-circle ml-1"
              style={{ fontSize: "0.6rem" }}
              title="Judged over paid sessions only, so a shift to free charging cannot make this look like a decline"
            />
          )}
        </span>
      </div>
    );
  };

  if (initialLoading) {
    return (
      <div className="flex justify-content-center p-5">
        <ProgressSpinner />
      </div>
    );
  }
  if (neverComputed) return <NeverComputedNotice months={neverComputed} onComputed={fetchWatchlist} />;
  if (error) return <Message severity="error" text={error} />;
  if (!report) return <Message severity="info" text="Nothing to show." />;

  return (
    <div>
      <div className="flex flex-wrap gap-4 align-items-end mb-3">
        <div className="flex flex-column gap-1">
          <label className="text-sm text-color-secondary">Evaluate as at</label>
          <Calendar
            value={asOfDate}
            viewDate={monthStart}
            minDate={monthStart}
            maxDate={monthEnd}
            onChange={(e) => setAsOfDate((e.value as Date) ?? null)}
            dateFormat="yy-mm-dd"
            placeholder={isCurrentMonth ? "Today" : "End of month"}
            showIcon
            showButtonBar
          />
        </div>
        <div className="flex flex-column gap-1">
          <label className="text-sm text-color-secondary">Flagged only</label>
          <InputSwitch checked={flaggedOnly} onChange={(e) => setFlaggedOnly(!!e.value)} />
        </div>
        <Button
          label="Refresh"
          icon="pi pi-refresh"
          outlined
          size="small"
          loading={refreshing}
          onClick={fetchWatchlist}
        />
      </div>

      <PaceWindowSummary
        window={report.window}
        asOf={report.asOf}
        currentMonth={report.currentMonth}
        previousMonth={report.previousMonth}
        thresholdPercent={report.thresholdPercent}
      />

      <DefinitionContextBanner context={report.context} />

      <ChangeBandLegend />

      <DataTable
        value={report.units}
        dataKey="unitId"
        paginator
        rows={25}
        selectionMode="single"
        onSelectionChange={(e) => onSelectUnit((e.value as WatchlistUnit)?.unitId)}
        emptyMessage={
          flaggedOnly
            ? "Nothing is currently falling short at this point in the month."
            : "No units to show."
        }
        size="small"
        scrollable
      >
        <Column field="stationName" header="Station" sortable frozen style={{ minWidth: "12rem" }} />
        {Object.keys(MEASURE_LABELS).map((measure) => (
          <Column
            key={measure}
            header={MEASURE_LABELS[measure]}
            style={{ minWidth: "10rem" }}
            body={(u: WatchlistUnit) => verdictBody(u, measure)}
          />
        ))}
        <Column
          header="Free share"
          style={{ minWidth: "11rem" }}
          body={(u: WatchlistUnit) => {
            const signal = u.freeShareSignal;
            const points = signal?.changePercentagePoints;
            return (
              <div className="flex flex-column">
                <span className="flex align-items-center gap-2">
                  <MeasureValueCell measure={signal?.currentPercent} format="percent" decimals={1} />
                  {signal?.material && (
                    <Tag
                      value="shifted"
                      severity="info"
                      title="A material move in free share. This explains a money change — it is not underperformance."
                    />
                  )}
                </span>
                {points !== null && points !== undefined && (
                  <span className="text-color-secondary text-xs">
                    {points > 0 ? "+" : ""}
                    {points.toFixed(1)} pts vs prev
                  </span>
                )}
              </div>
            );
          }}
        />
        <Column
          header="Cash (context)"
          style={{ minWidth: "11rem" }}
          body={(u: WatchlistUnit) => (
            <div
              className="flex flex-column text-color-secondary"
              title={u.moneyContext?.note ?? "Context only — does not drive any flag"}
            >
              <span>{euro(u.moneyContext?.moneyCents ?? 0)}</span>
              <span className="text-xs">was {euro(u.moneyContext?.previousWindowMoneyCents ?? 0)}</span>
            </div>
          )}
        />
        <Column
          header="Projection"
          style={{ minWidth: "12rem" }}
          body={(u: WatchlistUnit) => {
            const p = u.projection;
            if (!p || p.sessions === null) return <span className="text-color-secondary">—</span>;
            return (
              <div className="flex flex-column">
                <span title="Straight-line extrapolation of the current pace — a projection, not a forecast">
                  ~{p.sessions.toFixed(0)} sessions
                  <i className="pi pi-info-circle ml-1" style={{ fontSize: "0.6rem" }} />
                </span>
                <span className="text-color-secondary text-xs">
                  last month {p.previousMonthActual?.sessions ?? 0} actual
                </span>
              </div>
            );
          }}
        />
        <Column
          header="Flagged"
          style={{ minWidth: "10rem" }}
          body={(u: WatchlistUnit) =>
            u.flaggedMeasures?.length ? (
              <div className="flex flex-wrap gap-1">
                {u.flaggedMeasures.map((m) => (
                  <Tag key={m} value={MEASURE_LABELS[m] ?? m} severity="danger" />
                ))}
              </div>
            ) : (
              <span className="text-color-secondary text-xs">signal only</span>
            )
          }
        />
      </DataTable>
    </div>
  );
};

export default UnderperformanceWatchlist;
